import { useContext } from "react";

import "overlayscrollbars/overlayscrollbars.css";
import { firestoreUpdateTransaction } from "../firebaseio/firestoreIo";
import { UserContext } from "../firebaseio/UserContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { TransactionColumnSizer } from "./TransactionColumnSizer";
import { TransactionColumn } from "./TransactionColumn";
import { DropDownEntry, RyeDropDown } from "../rye/RyeDropDown";
import { RyeIcon } from "../rye/RyeIcon";
import { RuleHighlighter } from "./RuleHighlighter";
import { TransactionRowContext } from "./TransactionRow";

export function TransactionColumnValueCategory(props: {
  transactionId: string;
  categoryId: string | null;
  createNewRule: (categoryIdAction: string) => void;
  focusTransaction: () => void;
  selectPreviousTransaction: () => void;
  selectNextTransaction: () => void;
  saveDropDownRef: (inputRef: React.RefObject<HTMLInputElement>) => void;
}): JSX.Element {
  const user = useContext(UserContext);
  const categories = useContext(CategoriesContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  const { transactionSelected, ruleForFieldHighlighting } = useContext(
    TransactionRowContext,
  );

  function updateTransactionCategory(newCategoryId: string) {
    firestoreUpdateTransaction(
      user.uid,
      props.transactionId,
      {
        manual_data: {
          category_id: newCategoryId,
        },
      },
      null,
    );
  }

  function clearTransactionCategory() {
    firestoreUpdateTransaction(
      user.uid,
      props.transactionId,
      {
        manual_data: {
          category_id: null,
        },
      },
      null,
    );
  }

  function markTransactionReviewed(): void {
    firestoreUpdateTransaction(
      user.uid,
      props.transactionId,
      {
        manual_data: { reviewed: true },
      },
      null,
    );
  }

  function handleEnter(
    event: React.KeyboardEvent<HTMLDivElement>,
    entry: DropDownEntry<string> | null,
  ) {
    if (entry !== null) {
      markTransactionReviewed();
    }
    if (entry !== null && (event.ctrlKey || event.metaKey)) {
      props.createNewRule(entry.id);
    }
    if (event.shiftKey) {
      props.selectPreviousTransaction();
    } else {
      props.selectNextTransaction();
    }
  }

  function ruleHighlight(): boolean {
    return (
      ruleForFieldHighlighting.get !== null &&
      ruleForFieldHighlighting.get.action.category_id !== null
    );
  }

  function handleEntryClick() {
    markTransactionReviewed();
    props.focusTransaction();
  }

  return (
    <TransactionColumnSizer column={TransactionColumn.CATEGORY}>
      <RuleHighlighter ruleHighlight={ruleHighlight()} color={"green"}>
        <RyeDropDown<string>
          initEntryId={props.categoryId}
          entries={Array.from(categories.values())
            .sort((a, b) => a.order_position - b.order_position)
            .map((category) => {
              return {
                id: category.id,
                text: category.name,
                icon: category.emoji,
                groupId: category.category_group_id,
              };
            })}
          tooltip="Select category"
          groups={Array.from(categoryGroups.values())
            .sort((a, b) => a.order_position - b.order_position)
            .map((group) => ({
              id: group.id,
              label: group.name,
            }))}
          onSelectEntry={(entry) => updateTransactionCategory(entry.id)}
          searchable
          showNoneOption
          onSelectNoneOption={() => clearTransactionCategory()}
          width="full"
          height="full"
          variant={transactionSelected ? "outlined" : "transparent"}
          parentColor={transactionSelected ? "purple" : "normal"}
          onEnter={handleEnter}
          onEsc={props.focusTransaction} // this serves two purposes - it keeps the row highlighted when we push escape and it ensures when we go elsewhere the row went back to being focused so it correctly blurs and unhighlights
          onEntryClick={handleEntryClick}
          placeholder="Select category"
          placeholderClassName={
            transactionSelected
              ? ""
              : "placeholder:invisible group-hover/txrow:placeholder:visible"
          }
          placeholderIcon={
            <RyeIcon
              name="category"
              size="sm"
              className="text-on-surface-400"
            />
          }
          placeholderIconClassName={
            transactionSelected ? "" : "invisible group-hover/txrow:visible"
          }
          hookInputRef={props.saveDropDownRef}
        />
      </RuleHighlighter>
    </TransactionColumnSizer>
  );
}
